import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "sonner";

import appCss from "../styles.css?url";
import ogImage from "@/assets/hero-fast.webp";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { TopStrip } from "@/components/TopStrip";
import { WhatsappWidget } from "@/components/WhatsappWidget";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ProductModalProvider } from "@/context/ProductModalContext";
import { ProductModal } from "@/components/ProductModal";
import { PromoPopup } from "@/components/PromoPopup";
import { WebMcpTools } from "@/components/WebMcpTools";
import { CookieBanner } from "@/components/CookieBanner";
import { useProductsHidden } from "@/hooks/useSiteSettings";

function NotFoundComponent() {
  // Con il catalogo spento /prodotti rimanda in home: niente link verso una pagina che redirige.
  const productsHidden = useProductsHidden();
  return (
    <div className="relative min-h-[70vh] flex items-center justify-center bg-brand-cream px-4 paper-texture overflow-hidden">
      <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-brand-red/5 blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />
      <div className="relative max-w-xl text-center py-10">
        <p className="text-xs uppercase tracking-[0.3em] text-brand-warm-gray">Errore 404</p>
        <h1 className="font-display text-[7rem] md:text-[10rem] leading-none text-brand-red mt-2">404</h1>
        <div className="gold-divider mx-auto w-24 my-6 opacity-70" />
        <h2 className="font-display text-3xl md:text-4xl text-brand-charcoal">
          Questa pagina non è in bottega
        </h2>
        <p className="font-italic-serif italic text-brand-warm-gray text-lg mt-4">
          Forse il taglio che cerchi è già stato venduto, oppure ha cambiato banco.
        </p>
        <p className="mt-3 text-sm text-brand-warm-gray max-w-md mx-auto">
          Torna alla bottega: tra carni frollate, salumi tradizionali e L'Abatina,
          qualcosa di buono ti aspetta di sicuro.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-brand-red px-6 py-3 text-sm uppercase tracking-[0.18em] text-white hover:bg-brand-red-hover transition-colors"
          >
            Torna alla home
          </Link>
          {!productsHidden && (
            <Link
              to="/prodotti"
              className="inline-flex items-center justify-center rounded-md border border-brand-charcoal/30 px-6 py-3 text-sm uppercase tracking-[0.18em] text-brand-charcoal hover:border-brand-red hover:text-brand-red transition-colors"
            >
              Vedi i prodotti
            </Link>
          )}
          <Link
            to="/contatti"
            className="inline-flex items-center justify-center rounded-md border border-brand-charcoal/30 px-6 py-3 text-sm uppercase tracking-[0.18em] text-brand-charcoal hover:border-brand-red hover:text-brand-red transition-colors"
          >
            Contattaci
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-cream px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl text-brand-charcoal">
          Qualcosa non è andato a buon fine
        </h1>
        <p className="mt-2 text-sm text-brand-warm-gray">
          Riprova fra un istante o torna alla home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-brand-red px-4 py-2 text-sm text-white"
          >
            Riprova
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-brand-charcoal/30 px-4 py-2 text-sm text-brand-charcoal"
          >
            Home
          </a>
        </div>
      </div>
    </div>
  );
}

const SITE_URL = "https://labottegadellecarnidamarco.it";

const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": ["Butcher", "LocalBusiness", "FoodEstablishment"],
  "@id": `${SITE_URL}/#business`,
  name: "La Bottega delle Carni da Marco",
  alternateName: "Bottega delle Carni da Marco — Macelleria Campobasso",
  description:
    "Bottega artigianale di pregiate carni a Campobasso, dentro il Tigre di Via Kennedy. Carni frollate, salumi tradizionali e produzione de L'Abatina, salume riconosciuto da Slow Food e De.Co. Campobasso.",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  image: `${SITE_URL}/favicon.png`,
  telephone: "+39 340 748 7310",
  vatID: "IT01695340701",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Via John Fitzgerald Kennedy, 1",
    addressLocality: "Campobasso",
    postalCode: "86100",
    addressRegion: "CB",
    addressCountry: "IT",
  },
  geo: { "@type": "GeoCoordinates", latitude: 41.5603, longitude: 14.6684 },
  areaServed: [
    { "@type": "City", name: "Campobasso" },
    { "@type": "AdministrativeArea", name: "Molise" },
  ],
  sameAs: [
    "https://www.facebook.com/labottegadellecarnidamarco",
    "https://www.instagram.com/labottegadellecarnidamarcosrls/",
  ],
  currenciesAccepted: "EUR",
  paymentAccepted: "Contanti, Carte di credito, Bancomat",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "08:00",
      closes: "13:30",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "16:00",
      closes: "20:00",
    },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "00:00", closes: "00:00" },
  ],
  servesCuisine: ["Italian", "Molisana"],
  priceRange: "€€",
  knowsAbout: [
    "frollatura della carne",
    "salumi molisani",
    "L'Abatina",
    "carne bovina molisana",
    "macelleria artigianale",
  ],
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "La Bottega delle Carni da Marco",
  inLanguage: "it-IT",
  publisher: { "@id": `${SITE_URL}/#business` },
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "La Bottega delle Carni da Marco — Campobasso" },
      {
        name: "description",
        content:
          "Bottega artigianale di pregiate carni a Campobasso. Frollatura, salumi tradizionali e produzione de L'Abatina, presidio Slow Food.",
      },
      { name: "author", content: "La Bottega delle Carni da Marco" },
      { name: "theme-color", content: "#A8181C" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "geo.region", content: "IT-CB" },
      { name: "geo.placename", content: "Campobasso" },
      { name: "geo.position", content: "41.5603;14.6684" },
      { name: "ICBM", content: "41.5603, 14.6684" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "La Bottega delle Carni da Marco" },
      { property: "og:locale", content: "it_IT" },
      {
        property: "og:title",
        content: "La Bottega delle Carni da Marco — Campobasso",
      },
      {
        property: "og:description",
        content:
          "Carni frollate, salumi tradizionali e L'Abatina, il salume Slow Food. Una bottega indipendente nel cuore di Campobasso.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      // Immagine di anteprima predefinita per i link condivisi; le pagine prodotto/ricetta la sovrascrivono.
      { property: "og:image", content: `${SITE_URL}${ogImage}` },
      { name: "twitter:image", content: `${SITE_URL}${ogImage}` },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // Supabase: apre in anticipo la connessione (TLS + DNS) verso il backend.
      { rel: "preconnect", href: "https://qwhbranqzodbstbdjppv.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://qwhbranqzodbstbdjppv.supabase.co" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(localBusinessJsonLd),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(websiteJsonLd),
      },
      {
        // 1) Font ospitati sul sito (public/fonts/fonts.css): il CSS si aggiunge subito dopo il primo
        //    rendering, così non rallenta la comparsa della pagina (testo prima col font di sistema).
        // 2) Consent Mode v2: tutto «denied» finché l'utente non sceglie. Google Analytics
        // non è caricato qui: lo carica il banner dei cookie solo dopo il consenso
        // (src/components/CookieBanner.tsx, src/lib/consent.ts).
        children:
          "requestAnimationFrame(function(){setTimeout(function(){var s=document.createElement('link');s.rel='stylesheet';s.href='/fonts/fonts.css';document.head.appendChild(s);},0);});" +
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;" +
          "gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied'});" +
          // Banner dei cookie visibile da subito se manca una scelta valida (stessa regola di leggiConsenso).
          "try{var c=JSON.parse(localStorage.getItem('bottega-consenso-cookie')||'null');document.documentElement.setAttribute('data-cookie',c&&c.v===1&&Date.now()-Date.parse(c.data)<182*864e5?'si':'no');}catch(e){document.documentElement.setAttribute('data-cookie','no');}",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="it" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function ScrollToTop() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (typeof window !== "undefined" && !window.location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const transparent = pathname === "/" || pathname === "/abatina";
  const isAdmin = pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={200}>
          <ScrollToTop />
          <Outlet />
        </TooltipProvider>
        <Toaster
          position="bottom-right"
          theme="light"
          toastOptions={{
            style: {
              background: "#ffffff",
              color: "var(--brand-charcoal)",
              border: "1px solid rgba(42,37,32,0.12)",
              boxShadow: "0 10px 30px -10px rgba(42,37,32,0.15)",
            },
          }}
        />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <ProductModalProvider>
          <ScrollToTop />
          <div className="min-h-screen flex flex-col bg-brand-cream">
            <TopStrip />
            <Navbar transparent={transparent} />
            <main className={transparent ? "-mt-16 sm:-mt-20 xl:-mt-24" : ""}>
              <Outlet />
            </main>
            <Footer />
            <WhatsappWidget />
            <PromoPopup />
            <CookieBanner />
          </div>
          <ProductModal />
          <WebMcpTools />
        </ProductModalProvider>
      </TooltipProvider>
      <Toaster
        position="bottom-right"
        theme="light"
        toastOptions={{
          style: {
            background: "#ffffff",
            color: "var(--brand-charcoal)",
            border: "1px solid rgba(42,37,32,0.12)",
            boxShadow: "0 10px 30px -10px rgba(42,37,32,0.15)",
          },
        }}
      />
    </QueryClientProvider>
  );
}
